import { createEntityAdapter, createSlice } from "@reduxjs/toolkit";
import { APP_PAGE } from "constants/v2/appConstants";
import { merge, sortBy } from "lodash";
import { listRuleGeneratedContent } from "store/ruleConfiguration/actions";
import { getHLSToMP4VideoDetails, getMatchJobDetails, getStreamMatchJobDetails } from "store/stream/actions";
import { getStudioProjectLists } from "store/studioProjects/actions";

import {
  getClipListDetails,
  getClipLists,
  getInfinitePaginatedClipLists,
  getStreamClipLists,
} from "../clipList/actions";
import {
  deleteBulkClips,
  editClip,
  getArchivalClips,
  getArchivalFiltersList,
  getClipDetails,
  getClipNamingTemplates,
  getClips,
  getClipsByIds,
  getClipsByTimeRange,
  getFoldersByClipId,
  getPublishedClips,
  getPublishedClipsByStreamId,
  getRecentClipsLists,
  getUploadedClipDetail,
  updateClipDetails,
  updateClipsDetails,
} from "./actions";

export const clipEntityAdapter = createEntityAdapter({
  selectId: (entity) => entity._id,
});

const initialState = clipEntityAdapter.getInitialState({
  count: 0,
  promoCount: 0,
  publishedCount: 0,
  selectedClips: [],
  selectedClipsDetails: [],
  modals: {
    clipAlertModal: {
      isOpen: false,
      content: "",
    },
  },
  groupByDate: {},
  eventIds: [], // this is for zip url only
  cloudPublishEventIds: [], // this is for cloud publish only
  clipFolders: [],
  audioTrackVideoUrl: "",
  clipNamingTemplates: [],
  finalClipIds: [],
  archivalFiltersList: [],
  hlsSavedAsClipCount: 0,
  uploadedClipDetail: null,
});

export const clipSlice = createSlice({
  name: "clip",
  initialState: initialState,
  reducers: {
    setAudioTrackVideoUrl: (state, action) => {
      state.audioTrackVideoUrl = action.payload;
    },
    setSelectedClips: (state, action) => {
      state.selectedClips = action.payload;
    },
    addSelectedClips: (state, action) => {
      state.selectedClips = Array.from(new Set([...state.selectedClips.concat(action.payload)]));
    },
    removeSelectedClips: (state, action) => {
      state.selectedClips = state.selectedClips.filter((clipId) => !action.payload.includes(clipId));
    },
    setSelectedClipsDetails: (state, action) => {
      state.selectedClipsDetails = action.payload;
    },
    addSelectedClipsDetails: (state, action) => {
      const toAdd = action.payload;
      // Only add if no existing item has both the same contentId and aspectRatio
      const exists = state.selectedClipsDetails.some(
        (item) => item.contentId === toAdd.contentId && item.aspectRatio === toAdd.aspectRatio,
      );
      if (!exists) {
        state.selectedClipsDetails = [...state.selectedClipsDetails, toAdd];
      }
    },
    removeSelectedClipsDetails: (state, action) => {
      const toRemove = action.payload;
      // Remove all items that match both contentId and aspectRatio

      state.selectedClipsDetails = state.selectedClipsDetails.filter(
        (item) => !(item.contentId === toRemove.contentId && item.aspectRatio === toRemove.aspectRatio),
      );
    },
    showClipAllFiltersModal: (state, action) => {
      state.modals.clipAllFiltersModal = action.payload;
    },
    showClipAlertModal: (state, action) => {
      state.modals.clipAlertModal = action.payload;
    },
    showClipBulkDeleteModal: (state, action) => {
      state.modals.clipBulkDeleteModal = action.payload;
    },
    showClipCropperModal: (state, action) => {
      state.modals.clipCropperModal = action.payload;
    },
    showClipCopyToStreamModal: (state, action) => {
      state.modals.clipCopyToStreamModal = action.payload;
    },
    showClipCopyToChildModal: (state, action) => {
      state.modals.clipCopyToChildModal = action.payload;
    },
    showMovetoClipsPageModal: (state, action) => {
      state.modals.movetoClipsPageModal = action.payload;
    },
    showClipCloudPublishModal: (state, action) => {
      state.modals.clipCloudPublishModal = action.payload;
    },
    showClipCloudEditPublishModal: (state, action) => {
      state.modals.clipCloudEditPublishModal = action.payload;
    },
    showClipBulkDownloadModal: (state, action) => {
      state.modals.clipBulkDownloadModal = action.payload;
    },
    showClipTrimModal: (state, action) => {
      state.modals.clipTrimModal = action.payload;
    },
    showClipPreviewModal: (state, action) => {
      state.modals.clipPreviewModal = action.payload;
    },
    showClipChangeThumbnailModal: (state, action) => {
      state.modals.clipChangeThumbnailModal = action.payload;
    },
    showClipManageAndPreviewModal: (state, action) => {
      state.modals.clipManageAndPreviewModal = action.payload;
    },
    showDragDropHLPreviewModal: (state, action) => {
      state.modals.dragDropHLPreviewModal = action.payload;
    },
    incrementSelectedClipIndexAndUpdateManagePreviewModal: (state, action) => {
      const incrementVal = action.payload?.incrementLenght || 1;
      const modifiedClipIndex = state.modals.clipManageAndPreviewModal.selectedClipIndex + incrementVal;

      if (modifiedClipIndex > state.ids.length - 1) {
        return;
      }

      state.modals.clipManageAndPreviewModal = {
        ...state.modals.clipManageAndPreviewModal,
        selectedClipIndex: modifiedClipIndex,
        clipId: state.ids[modifiedClipIndex],
        contentId: state.ids[modifiedClipIndex],
      };
    },
    decrementSelectedClipIndexAndUpdateManagePreviewModal: (state, action) => {
      const decrementVal = action.payload?.decrementLength || 1;
      const modifiedClipIndex = state.modals.clipManageAndPreviewModal.selectedClipIndex - decrementVal;

      if (modifiedClipIndex < 0) {
        return;
      }

      state.modals.clipManageAndPreviewModal = {
        ...state.modals.clipManageAndPreviewModal,
        selectedClipIndex: modifiedClipIndex,
        clipId: state.ids[modifiedClipIndex],
        contentId: state.ids[modifiedClipIndex],
      };
    },
    setCurrentClipIdForClipCDTeamCustomEditModal: (state, action) => {
      const { clipId, clipIndex, streamId, referenceStreamId } = action.payload;

      state.modals.clipCDTeamCustomEditModal = {
        ...state.modals.clipCDTeamCustomEditModal,
        selectedClipIndex: clipIndex,
        clipId,
        streamId,
        referenceStreamId,
      };
    },
    showClipEnhancedTaggingModal: (state, action) => {
      state.modals.clipEnhancedTaggingModal = action.payload;
    },
    showClipsInQueueFilterModal: (state, action) => {
      state.modals.clipsInQueueFilterModal = action.payload;
    },
    showClipCDTeamCustomEditModal: (state, action) => {
      state.modals.clipCDTeamCustomEditModal = action.payload;
    },
    showClipRatingOverwriteWarningModal: (state, action) => {
      state.modals.clipRatingOverwriteWarningModal = action.payload;
    },
    showClipDeleteEditedClipModal: (state, action) => {
      state.modals.clipDeleteEditedClipModal = action.payload;
    },
    showClipOverwriteEditedClipModal: (state, action) => {
      state.modals.clipOverwriteEditedClipModal = action.payload;
    },
    showClipCroppedClipPreviewModal: (state, action) => {
      state.modals.clipCroppedClipPreviewModal = action.payload;
    },
    showClipMetadataModal: (state, action) => {
      state.modals.clipMetadataModal = action.payload;
    },
    showClipFtpPublishModal: (state, action) => {
      state.modals.clipFtpPublishModal = action.payload;
    },
    showClipSftpPublishModal: (state, action) => {
      state.modals.clipSftpPublishModal = action.payload;
    },
    showClipCMSPublishModal: (state, action) => {
      state.modals.clipCMSPublishModal = action.payload;
    },
    showClipEditorTrimClipConfirmationModal: (state, action) => {
      state.modals.clipEditorTrimClipConfirmationModal = action.payload;
    },
    showUploadClipsModal: (state, action) => {
      state.modals.uploadClipsModal = action.payload;
    },
    showUploadMultiClipsModal: (state, action) => {
      state.modals.uploadMultiClipsModal = action.payload;
    },
    setClipsGroupByDate: (state, action) => {
      state.groupByDate = action.payload;
    },
    showDeleteConvertVideoModal: (state, action) => {
      state.modals.clipDeleteConvertVideoModal = action.payload;
    },
    showWebStoriesAllFiltersModal: (state, action) => {
      state.modals.webStoriesAllFiltersModal = action.payload;
    },
    showPublishOnedriveModal: (state, action) => {
      state.modals.publishOnedriveModal = action.payload;
    },
    showClipExportTemplatesModal: (state, action) => {
      state.modals.clipExportTemplatesModal = action.payload;
    },
    showClipAudioTranslationModal: (state, action) => {
      state.modals.clipAudioTranslationModal = action.payload;
    },
    showClipThumbnailTemplateApplyModal: (state, action) => {
      state.modals.clipThumbnailTemplateApplyModal = action.payload;
    },
    showClipCutMarkersConfirmModal: (state, action) => {
      state.modals.clipCutMarkersConfirmModal = action.payload;
    },
    showClipReportIssueModal: (state, action) => {
      state.modals.clipReportIssueModal = action.payload;
    },
    storeEventId: (state, action) => {
      state.eventIds.push(action.payload);
    },
    removeEventId: (state, action) => {
      state.eventIds = state.eventIds.filter((id) => id !== action.payload);
    },
    storeCloudPublishEventId: (state, action) => {
      state.cloudPublishEventIds.push(action.payload);
    },
    removeCloudPublishEventId: (state, action) => {
      state.cloudPublishEventIds = state.cloudPublishEventIds.filter((id) => id !== action.payload);
    },
    updateClipValues: (state, action) => {
      const { isPreview, progressPercent, previewUrl, clipId } = action.payload;

      clipEntityAdapter.updateOne(state, {
        id: clipId,
        changes: { isPreview, progressPercent, previewUrl },
      });
    },
    updateClipProgressPercent: (state, action) => {
      const { progressPercent, clipId } = action.payload;

      clipEntityAdapter.updateOne(state, {
        id: clipId,
        changes: { progressPercent },
      });
    },
    updateClipTitle: (state, action) => {
      const { clipId, episodeTitle } = action.payload;

      clipEntityAdapter.updateOne(state, {
        id: clipId,
        changes: { episodeTitle },
      });
    },
    updateClipIsLiveProgress: (state, action) => {
      const { clipId, isLiveProcess } = action.payload;

      clipEntityAdapter.updateOne(state, {
        id: clipId,
        changes: { isLiveProcess },
      });
    },
    setHlsSavedAsClipCount: (state, action) => {
      state.hlsSavedAsClipCount = action.payload;
    },
    removeDatafeedClips: (state, action) => {
      const { time } = action.payload;

      const clipsToRemove = Object.values(state.entities)
        .filter((clip) => clip.start_time >= time && clip?.datafeed?.type === "datafeed")
        .map((clip) => clip._id);

      clipEntityAdapter.removeMany(state, clipsToRemove);
      state.finalClipIds = state.finalClipIds.filter((clipId) => !clipsToRemove.includes(clipId));
    },
    updateClipProgress: (state, action) => {
      const { clipId, progress } = action.payload;

      clipEntityAdapter.updateOne(state, {
        id: clipId,
        changes: { progress },
      });
    },
    updateClipListClipsEntities: (state, action) => {
      const clipsListData = action.payload;
      if (clipsListData) {
        state.entities[clipsListData._id] = {
          ...state.entities[clipsListData._id],
          ...clipsListData,
          clips: clipsListData?.clips?.map((clip) => clip?._id),
        };

        clipsListData.clips.forEach((clip) => {
          state.entities[clip._id] = {
            ...state.entities[clip._id],
            ...clip,
          };
        });
      }
    },

    updateClipVariantUrl: (state, action) => {
      const { clipId, variantUrl } = action.payload;

      clipEntityAdapter.updateOne(state, {
        id: clipId,
        changes: { variantUrl },
      });
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getClips.fulfilled, (state, action) => {
      if (action.payload.success) {
        let incomingClips = action.payload.entities?.clip || {};

        if (action.meta.arg.dontRetainSelectedClips) {
          const selectedClips = state.selectedClips;
          const filteredSelectedClips = selectedClips.filter((clipId) => incomingClips[clipId]);

          state.selectedClips = filteredSelectedClips;
        }

        // Handle selectedClipsDetails filtering
        if (action.meta.arg.dontRetainSelectedClips) {
          const selectedClipsDetails = state.selectedClipsDetails;
          const filteredSelectedClipsDetails = selectedClipsDetails.filter(
            (clipDetail) => incomingClips[clipDetail.contentId],
          );

          state.selectedClipsDetails = filteredSelectedClipsDetails;
        }

        const selectedClips = state.selectedClips;

        const clipsToRetain = selectedClips.reduce((acc, clipId) => {
          if (state.entities[clipId]) {
            acc[clipId] = { ...state.entities[clipId], retainedClip: true };
          }

          return acc;
        }, {});

        const finalClipsArray = [];

        Object.keys(incomingClips).forEach((clipId) => {
          if (!selectedClips.includes(clipId)) {
            finalClipsArray.push({
              ...incomingClips[clipId],
              isHighlightClip: false,
            });
          } else {
            finalClipsArray.push({
              ...clipsToRetain[clipId],
              ...incomingClips[clipId],
              retainedClip: false,
              isHighlightClip: false,
            });
          }
        });

        // Add selected/retained clips to the final array if they are not already present
        Object.keys(clipsToRetain).forEach((clipId) => {
          if (!finalClipsArray.some((clip) => clip?._id === clipId)) {
            finalClipsArray.push({
              ...clipsToRetain[clipId],
              retainedClip: true,
            });
          }
        });

        if (action.meta.arg.isMergeEntities) {
          const finalClipsObject = finalClipsArray.reduce((acc, clip) => {
            acc[clip._id] = clip;

            return acc;
          }, {});

          state.entities = {
            ...state.entities,
            ...finalClipsObject,
          };

          state.promoCount = action.payload.totalLength;
        } else {
          if (action.meta.arg.isLiveVideoPage) {
            const deeplyMergedEntities = finalClipsArray.reduce((acc, clip) => {
              acc[clip._id] = merge({}, state.entities[clip._id], clip);

              return acc;
            }, {});

            state.entities = {
              ...state.entities,
              ...deeplyMergedEntities,
            };
            state.ids = finalClipsArray.map((clip) => clip._id);
          } else {
            if (action.meta.arg.upsert) {
              clipEntityAdapter.upsertMany(state, finalClipsArray);
            } else {
              clipEntityAdapter.setAll(state, finalClipsArray);
            }
          }

          const notRetainedClips = finalClipsArray.filter((clip) => !clip.retainedClip);

          state.count = Math.max(action.payload.totalLength, notRetainedClips.length);

          if (action.meta.arg.isPromoSubSection) {
            state.promoCount = action.payload.totalLength;
          }
        }
        state.filterCounts = action.payload.filterCounts || {};
      }
    });
    builder.addCase(getClipsByIds.fulfilled, (state, action) => {
      if (action?.meta?.arg?.append) {
        clipEntityAdapter.upsertMany(state, action.payload?.data || []);
      } else {
        let incomingClips = action.payload?.data || [];
        let incomingClipsMap = incomingClips.reduce((acc, clip) => {
          acc[clip._id] = clip;

          return acc;
        }, {});

        if (action.meta.arg.dontRetainSelectedClips) {
          state.selectedClips = state.selectedClips.filter((clipId) => incomingClipsMap[clipId]);
        }

        const clipsToRetain = state.selectedClips.reduce((acc, clipId) => {
          if (state.entities[clipId]) {
            acc[clipId] = { ...state.entities[clipId], retainedClip: true };
          }

          return acc;
        }, {});

        const finalClipsArray = [];

        incomingClips.forEach((clip) => {
          const clipId = clip._id;

          if (!state.selectedClips.includes(clipId)) {
            finalClipsArray.push({
              ...clip,
              isHighlightClip: false,
            });
          } else {
            finalClipsArray.push({
              ...clipsToRetain[clipId],
              ...clip,
              retainedClip: false,
              isHighlightClip: false,
            });
          }
        });

        // Add selected/retained clips to the final array if they are not already present
        Object.keys(clipsToRetain).forEach((clipId) => {
          if (!finalClipsArray.some((clip) => clip?._id === clipId)) {
            finalClipsArray.push({
              ...clipsToRetain[clipId],
              retainedClip: true,
            });
          }
        });

        const clipOrder = action.meta?.arg?.payload?.clipIds || [];
        const clips = sortBy(finalClipsArray || [], (clip) => {
          const index = clipOrder.indexOf(clip._id);

          return index === -1 ? clipOrder.length : index;
        });

        if (action.meta.arg.upsert) {
          clipEntityAdapter.upsertMany(state, clips);
        } else {
          clipEntityAdapter.setAll(state, clips);
        }

        const notRetainedClips = clips.filter((clip) => !clip.retainedClip);

        state.count = notRetainedClips.length;
      }
    });
    builder.addCase(getStreamClipLists.fulfilled, (state, action) => {
      if (action.payload.entities?.clip) {
        Object.keys(action.payload.entities.clip).forEach((clipId) => {
          state.entities[clipId] = {
            ...state.entities[clipId],
            ...action.payload.entities.clip[clipId],
            ...(state.entities[clipId] ? {} : { isHighlightClip: true }),
          };
        });
      }
    });

    builder.addCase(getClipLists.fulfilled, (state, action) => {
      if (action.payload.entities?.clip) {
        Object.keys(action.payload.entities.clip).forEach((clipId) => {
          state.entities[clipId] = {
            ...state.entities[clipId],
            ...action.payload.entities.clip[clipId],
          };
        });
      }
    });
    builder.addCase(getClipListDetails.fulfilled, (state, action) => {
      if (action.payload.success) {
        const { entities } = action.payload;
        const clipLists = entities?.clipList || {};
        const clips = entities?.clip || {};

        Object.keys(clipLists).forEach((clipListId) => {
          const clipList = clipLists[clipListId];

          state.entities[clipListId] = clipList;
        });

        if (clips && Object.keys(clips).length > 0) {
          Object.keys(clips).forEach((clipId) => {
            if (state.entities[clipId]) {
              state.entities[clipId] = {
                ...state.entities[clipId],
                ...clips[clipId],
              };
            } else if (!action.meta.arg.isDragDropHLGeneration) {
              state.entities[clipId] = clips[clipId];
            }
          });
        }
      }
    });

    builder.addCase(getClipDetails.fulfilled, (state, action) => {
      if (action.payload.success) {
        clipEntityAdapter.setOne(state, action.payload.data);
      }
    });
    builder.addCase(getRecentClipsLists.fulfilled, (state, action) => {
      if (action.payload.success) {
        if (action.payload?.clips) {
          /**
           * If its a new req with updated filters, them don't append in prev state
           */
          const isNewReq = action?.meta?.arg?.payload?.params?.pageNo === 1;
          const appendInExisting = action?.meta?.arg?.payload?.appendInExisting;

          if (isNewReq && !appendInExisting) {
            clipEntityAdapter.setAll(state, action.payload.entities?.clipList || {});
            state.groupByDate = action.payload.entities?.clipListGroupByDate || {};
          } else {
            // for infinite scroll pagination
            clipEntityAdapter.setMany(state, action.payload.entities?.clipList || {});
            state.groupByDate = {
              ...state.groupByDate,
              ...(action.payload.entities?.clipListGroupByDate || {}),
            };
          }

          state.count = action.payload.totalLength;
        } else {
          state.ids = [];
          state.count = 0;
          state.entities = {};
          state.groupByDate = {};
        }
      }
    });
    builder.addCase(getStudioProjectLists.fulfilled, (state, action) => {
      if (action.payload.success) {
        if (action.payload?.groupByDatepProjects) {
          /**
           * If its a new req with updated filters, them don't append in prev state
           */
          const isNewReq = action?.meta?.arg?.payload?.params?.pageNo === 1;
          const appendInExisting = action?.meta?.arg?.payload?.appendInExisting;

          if (isNewReq && !appendInExisting) {
            clipEntityAdapter.setAll(state, action.payload.entities?.studioProjectClips || {});
          } else {
            // for infinite scroll pagination
            clipEntityAdapter.setMany(state, action.payload.entities?.studioProjectClips || {});
          }

          state.count = action.payload.totalLength;
        } else {
          state.ids = [];
          state.count = 0;
          state.entities = {};
        }
      }
    });
    builder.addCase(getInfinitePaginatedClipLists.fulfilled, (state, action) => {
      if (action.payload.entities?.clip) {
        Object.keys(action.payload.entities.clip).forEach((clipId) => {
          state.entities[clipId] = {
            ...state.entities[clipId],
            ...action.payload.entities.clip[clipId],
          };
        });
      }
    });
    builder.addCase(updateClipDetails.fulfilled, (state, action) => {
      if (action.payload.data) {
        state.entities[`${action.payload.data._id}`].episodeTitle = action.payload.data.episodeTitle;
      }
    });
    builder.addCase(getStreamMatchJobDetails.fulfilled, (state, action) => {
      if (action?.meta?.arg?.ignoreRedux) return;

      clipEntityAdapter.removeAll(state); // Only clear on the first call
      if (action.payload?.data?.data) {
        const data = action.payload.data?.data?.map((item) => ({
          ...item,
          type: "matchVideo",
          videoThumbnailUrl: item?.jobDetails?.thumbnail,
        }));

        clipEntityAdapter.addMany(state, data);
        state.count = (state.count || 0) + (action.payload.data?.data?.length || 0);
      }
    });

    builder.addCase(getMatchJobDetails.fulfilled, (state, action) => {
      if (action.payload?.data?.data) {
        clipEntityAdapter.setOne(state, {
          ...action.payload.data.data,
          type: "matchVideo",
          _id: action.payload.data.data.id,
          videoUrl: action.payload.data.data?.jobDetails?.CDN_URL,
          videoThumbnailUrl: action.payload.data.data?.jobDetails?.thumbnail,
        });
      }
    });

    builder.addCase(getHLSToMP4VideoDetails.fulfilled, (state, action) => {
      if (action.payload.data) {
        const data = action.payload.data?.map((item) => ({
          ...item,
          videoUrl: item.mp4VideoUrl,
        }));

        clipEntityAdapter.addMany(state, data);
        state.count = (state.count || 0) + (action.payload.data.length || 0);
      }
    });
    builder.addCase(getArchivalClips.fulfilled, (state, action) => {
      if (action.payload.success) {
        const incomingClips = action.payload.clips || {};
        const selectedClips = state.selectedClips;

        const clipsToRetain = selectedClips.reduce((acc, clipId) => {
          if (state.entities[clipId]) {
            acc[clipId] = { ...state.entities[clipId], retainedClip: true };
          }

          return acc;
        }, {});

        const finalClipsArray = [];

        Object.keys(incomingClips).forEach((clipId) => {
          if (!selectedClips.includes(clipId)) {
            finalClipsArray.push({
              ...incomingClips[clipId],
              isHighlightClip: false,
            });
          } else {
            finalClipsArray.push({
              ...clipsToRetain[clipId],
              ...incomingClips[clipId],
              retainedClip: false,
              isHighlightClip: false,
            });
          }
        });

        // Add selected/retained clips to the final array if they are not already present
        Object.keys(clipsToRetain).forEach((clipId) => {
          if (!finalClipsArray.some((clip) => clip?._id === clipId)) {
            finalClipsArray.push({ ...clipsToRetain[clipId], retainedClip: true });
          }
        });

        if (action.meta.arg.isMergeEntities) {
          const finalClipsObject = finalClipsArray.reduce((acc, clip) => {
            acc[clip._id] = clip;

            return acc;
          }, {});

          state.entities = {
            ...state.entities,
            ...finalClipsObject,
          };
        } else {
          clipEntityAdapter.setAll(state, finalClipsArray);
        }

        const numberOfRetainedClips = finalClipsArray.length;

        state.count = Math.max(action.payload.count, numberOfRetainedClips);
        state.filterCounts = action.payload.filterCounts || {};
      }
    });
    builder.addCase(getPublishedClips.fulfilled, (state, action) => {
      if (action.payload.data) {
        if (action.meta.arg.payload.type === "highlights") {
          state.entities = {
            ...state.entities,
            ...action.payload.data,
          };
        } else {
          clipEntityAdapter.setAll(state, action.payload.data);
        }

        state.count = action.payload.data.length;
      }
    });
    builder.addCase(getFoldersByClipId.fulfilled, (state, action) => {
      state.clipFolders = action.payload.data;
    });
    builder.addCase(updateClipsDetails.fulfilled, (state, action) => {
      if (action.payload.data) {
        state.entities[`${action.payload.data._id}`].episodeTitle = action.payload.data.episodeTitle;
      }
    });
    builder.addCase(getClipNamingTemplates.fulfilled, (state, action) => {
      if (action.payload.data) {
        state.clipNamingTemplates = action.payload.data;
      }
    });
    builder.addCase(getClipsByTimeRange.fulfilled, (state, action) => {
      if (action.payload.success) {
        const finalClipIds = action.payload?.result;
        const finalClipEntities = action.payload?.entities?.clip;

        state.entities = merge({}, state.entities, finalClipEntities);
        state.finalClipIds = finalClipIds;
      }
    });
    builder.addCase(getArchivalFiltersList.fulfilled, (state, action) => {
      if (action.payload?.filters) {
        state.archivalFiltersList = action.payload.filters;
      }
    });
    builder.addCase(getPublishedClipsByStreamId.fulfilled, (state, action) => {
      if (action.payload.data) {
        clipEntityAdapter.setAll(state, action.payload.data);
        state.publishedCount = action.payload.totalLength;
      }
    });
    builder.addCase(editClip.pending, (state, action) => {
      const payload = action.meta.arg.payload;
      if (payload?._id || payload?.id) {
        clipEntityAdapter.updateOne(state, {
          id: payload?._id || payload?.id,
          changes: { ...(payload.hasOwnProperty("isBookmarked") ? { isBookmarked: payload.isBookmarked } : {}) },
        });
      }
    });
    builder.addCase(editClip.rejected, (state, action) => {
      const payload = action.meta.arg.payload;
      if (payload?._id || payload?.id) {
        clipEntityAdapter.updateOne(state, {
          id: payload?._id || payload?.id,
          changes: { ...(payload.hasOwnProperty("isBookmarked") ? { isBookmarked: !payload.isBookmarked } : {}) },
        });
      }
    });
    builder.addCase(editClip.fulfilled, (state, action) => {
      const payload = action.meta.arg.payload;
      const clipId = action.meta.arg.clipId;
      if (clipId && payload?.videoThumbnailUrl) {
        clipEntityAdapter.updateOne(state, {
          id: clipId,
          changes: {
            videoThumbnailUrl: payload?.videoThumbnailUrl,
          },
        });
      }
    });
    builder.addCase(deleteBulkClips.fulfilled, (state, action) => {
      if (
        action.payload.success &&
        (action.meta.arg.page === APP_PAGE.LIVE_VIDEO ||
          action.meta.arg.page === APP_PAGE.LIVE_EDIT ||
          action.meta.arg.page === APP_PAGE.CLIPS ||
          action.meta.arg.page === APP_PAGE.EDIT_CLIPS)
      ) {
        const payload = action.meta.arg.payload;
        const deletedClipIds = payload?.id || [];

        state.finalClipIds = state.finalClipIds.filter((clipId) => !deletedClipIds.includes(clipId));
        clipEntityAdapter.removeMany(state, deletedClipIds);
      }
    });

    builder.addCase(getUploadedClipDetail.fulfilled, (state, action) => {
      if (action.payload.data) {
        state.uploadedClipDetail = action.payload.data;
      }
    });

    builder.addCase(listRuleGeneratedContent.fulfilled, (state, action) => {
      if (action.payload.entities?.clip) {
        Object.keys(action.payload.entities.clip).forEach((clipId) => {
          state.entities[clipId] = {
            ...state.entities[clipId],
            ...action.payload.entities.clip[clipId],
          };
        });
      }
    });
  },
});

export const {
  setSelectedClips,
  addSelectedClips,
  removeSelectedClips,
  showClipAllFiltersModal,
  showClipAlertModal,
  showClipBulkDeleteModal,
  showClipCropperModal,
  showClipCopyToStreamModal,
  showClipCloudPublishModal,
  showClipCloudEditPublishModal,
  showClipCopyToChildModal,
  showClipBulkDownloadModal,
  showClipTrimModal,
  showClipPreviewModal,
  showClipChangeThumbnailModal,
  showClipEnhancedTaggingModal,
  showClipCDTeamCustomEditModal,
  showClipDeleteEditedClipModal,
  showClipOverwriteEditedClipModal,
  showClipSaveEditedClipModal,
  showClipCroppedClipPreviewModal,
  showClipMetadataModal,
  showClipFtpPublishModal,
  showClipSftpPublishModal,
  showClipCMSPublishModal,
  showClipEditorTrimClipConfirmationModal,
  showUploadClipsModal,
  showUploadMultiClipsModal,
  showDeleteConvertVideoModal,
  showPublishOnedriveModal,
  setClipsGroupByDate,
  showClipManageAndPreviewModal,
  showClipReportIssueModal,
  incrementSelectedClipIndexAndUpdateManagePreviewModal,
  decrementSelectedClipIndexAndUpdateManagePreviewModal,
  setCurrentClipIdForClipCDTeamCustomEditModal,
  showClipRatingOverwriteWarningModal,
  showWebStoriesAllFiltersModal,
  showClipExportTemplatesModal,
  showClipAudioTranslationModal,
  storeEventId,
  removeEventId,
  updateClipValues,
  updateClipProgressPercent,
  updateClipTitle,
  updateClipIsLiveProgress,
  setAudioTrackVideoUrl,
  setSubTitleVideoUrl,
  showMovetoClipsPageModal,
  showDragDropHLPreviewModal,
  showClipsInQueueFilterModal,
  storeCloudPublishEventId,
  removeCloudPublishEventId,
  showClipThumbnailTemplateApplyModal,
  setHlsSavedAsClipCount,
  removeDatafeedClips,
  showClipCutMarkersConfirmModal,
  addSelectedClipsDetails,
  removeSelectedClipsDetails,
  setSelectedClipsDetails,
  updateClipProgress,
  updateClipListClipsEntities,
  updateClipVariantUrl,
} = clipSlice.actions;

export default clipSlice.reducer;
